// pages/LooseStoneList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { FaPencilAlt, FaTrashAlt, FaFilter, FaTimesCircle } from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import { TbArrowsSort } from "react-icons/tb";
import Select from "react-select";
import toast from "react-hot-toast";

import ConfirmDialog from "../components/ConfirmDialog";

// API hooks (loose-stone)
import {
  useGetLooseStoneEntriesQuery,
  useDeleteLooseStoneEntryMutation,
  useBulkDeleteLooseStoneEntriesMutation,
} from "../toolkitServices/looseStoneApi";

/* masters */
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllStonesQuery } from "../toolkitServices/stoneApi";
import { useGetStoneAttributesQuery } from "../toolkitServices/stoneAttributeApi";

import { useDispatch, useSelector } from "react-redux";
import { setVendorList } from "../slices/vendorSlice";
import { setBranchList } from "../slices/branchSlice";
import { setStoneList } from "../slices/stoneSlice";
import { setStoneAttrList } from "../slices/stoneAttrSlice";
import { handleDropDowns } from "../config/dropDownHelpers";

/* ----------------- helpers & styles ----------------- */
const headerCell = "px-4 py-3 text-xs font-semibold uppercase tracking-wide";
const dataCell = "px-4 py-2";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 10,
    borderColor: state.isFocused ? "#7c6ac8" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(124,106,200,.2)" : "none",
    "&:hover": { borderColor: "#cfc7f0" },
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

const SkeletonRows = ({ cols = 13, rows = 8 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={`${dataCell}`}>
            <div className="h-3 w-24 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const LooseStoneList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // keyboard: Ctrl+P → create entry
  useEffect(() => {
    const onKeys = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/loose-stones/create");
      }
    };
    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [navigate]);

  /* ---------- header controls ---------- */
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // sorting
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // filters (draft vs applied)
  const emptyFilter = {
    vendorId: "",
    branchId: "",
    stoneId: "",
    shape: "",
    color: "",
    size: "",
    settingType: "",
    dateFrom: "",
    dateTo: "",
  };
  const [appliedFilters, setAppliedFilters] = useState(emptyFilter);
  const [draftFilters, setDraftFilters] = useState(emptyFilter);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // masters -> store
  const { data: vendors } = useGetAllVendorsQuery();
  const { data: branches } = useGetAllBranchesQuery();
  const { data: stones } = useGetAllStonesQuery();
  const { data: attrs } = useGetStoneAttributesQuery();

  useEffect(() => {
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
    if (stones?.data?.length) dispatch(setStoneList(stones.data));
    if (attrs?.data?.length) dispatch(setStoneAttrList(attrs.data));
  }, [vendors, branches, stones, attrs, dispatch]);

  const vendorsFromState = useSelector((s) => s.vendors?.vendors) || [];
  const branchesFromState = useSelector((s) => s.branches?.branches) || [];
  const stonesFromState = useSelector((s) => s.stones?.stones) || [];
  const stoneAttrFromState = useSelector((s) => s.stoneAttr?.stoneAttr) || [];

  const vendorOptions = useMemo(
    () => handleDropDowns({ key: "vendor", vendors: vendorsFromState }),
    [vendorsFromState]
  );
  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState }),
    [branchesFromState]
  );
  const stoneOptions = useMemo(
    () =>
      (stonesFromState || []).map((s) => ({
        label: s.stoneName,
        value: s._id,
      })),
    [stonesFromState]
  );

  const shapesOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "shape" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );
  const colorsOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "color" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );
  const sizesOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "size" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );
  const settingTypeOptions = useMemo(
    () =>
      (stoneAttrFromState || [])
        .filter((x) => x.type === "settingType" && x.isActive !== false)
        .map((x) => ({ label: x.name, value: x._id })),
    [stoneAttrFromState]
  );

  /* ---------- query ---------- */
  const queryParams = {
    page,
    limit,
    q: search, // server uses 'q' for text search in controller
    sortBy,
    sortOrder,
    ...appliedFilters, // server only uses supported keys
  };
  const { data, isFetching, isLoading, isSuccess } = useGetLooseStoneEntriesQuery(queryParams);

  const rows = data?.data || data?.items || [];
  const totalDocs =
    data?.pagination?.totalDocs ?? data?.total ?? rows.length ?? 0;
  const totalPages =
    data?.pagination?.totalPages ??
    data?.pages ??
    Math.max(1, Math.ceil(totalDocs / limit));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  // selections (bulk delete) — MUST be after rows is defined
  const [selectedIds, setSelectedIds] = useState(new Set());

  const deletableIdsOnPage = useMemo(
    () => (rows || []).map((r) => String(r._id)),
    [rows]
  );

  const allSelectedOnPage =
    deletableIdsOnPage.length > 0 &&
    deletableIdsOnPage.every((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        deletableIdsOnPage.forEach((id) => next.delete(id));
      } else {
        deletableIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // debounce page reset on filter/sort/search changes
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 300);
    return () => clearTimeout(t);
  }, [search, limit, sortBy, sortOrder, appliedFilters]);

  // clear selection when query result changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, limit, sortBy, sortOrder, search, isSuccess, appliedFilters]);

  // sorting
  const onSort = (field) => {
    if (sortBy === field) setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  // filters modal handlers
  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setIsFilterOpen(false);
  };
  const resetDraftFilters = () => setDraftFilters(emptyFilter);

  // chips
  const findLabel = (opts, v) =>
    (opts || []).find((o) => String(o.value) === String(v))?.label || "";
  const chips = useMemo(() => {
    const list = [];
    if (search) list.push({ key: "search", label: `Search: ${search}` });
    if (appliedFilters.vendorId)
      list.push({
        key: "vendorId",
        label: `Vendor: ${findLabel(vendorOptions, appliedFilters.vendorId)}`,
      });
    if (appliedFilters.branchId)
      list.push({
        key: "branchId",
        label: `Branch: ${findLabel(branchOptions, appliedFilters.branchId)}`,
      });
    if (appliedFilters.stoneId)
      list.push({
        key: "stoneId",
        label: `Stone: ${findLabel(stoneOptions, appliedFilters.stoneId)}`,
      });
    if (appliedFilters.shape)
      list.push({
        key: "shape",
        label: `Shape: ${findLabel(shapesOptions, appliedFilters.shape)}`,
      });
    if (appliedFilters.color)
      list.push({
        key: "color",
        label: `Color: ${findLabel(colorsOptions, appliedFilters.color)}`,
      });
    if (appliedFilters.size)
      list.push({
        key: "size",
        label: `Size: ${findLabel(sizesOptions, appliedFilters.size)}`,
      });
    if (appliedFilters.settingType)
      list.push({
        key: "settingType",
        label: `Setting: ${findLabel(
          settingTypeOptions,
          appliedFilters.settingType
        )}`,
      });
    if (appliedFilters.dateFrom)
      list.push({ key: "dateFrom", label: `From: ${appliedFilters.dateFrom}` });
    if (appliedFilters.dateTo)
      list.push({ key: "dateTo", label: `To: ${appliedFilters.dateTo}` });
    return list;
  }, [
    appliedFilters,
    search,
    vendorOptions,
    branchOptions,
    stoneOptions,
    shapesOptions,
    colorsOptions,
    sizesOptions,
    settingTypeOptions,
  ]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    else setAppliedFilters((s) => ({ ...s, [key]: "" }));
    setPage(1);
  };
  const clearAllChips = () => {
    setSearch("");
    setAppliedFilters(emptyFilter);
    setPage(1);
  };

  // mutations
  const [deleteEntry, { isLoading: deletingOne }] = useDeleteLooseStoneEntryMutation();
  const [bulkDelete, { isLoading: deletingMany }] =
    useBulkDeleteLooseStoneEntriesMutation();

  // confirm dialog
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null, // 'single' | 'bulk'
    payload: null,
  });
  const openConfirmSingle = (item) =>
    setConfirm({
      open: true,
      mode: "single",
      payload: { id: item._id, label: item.stoneName },
    });
  const openConfirmBulk = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setConfirm({ open: true, mode: "bulk", payload: { ids, count: ids.length } });
  };
  const closeConfirm = () => setConfirm({ open: false, mode: null, payload: null });

  const handleConfirm = async () => {
    try {
      if (confirm.mode === "single") {
        const { id, label } = confirm.payload || {};
        await deleteEntry(id).unwrap();
        toast.success(`Deleted ${label}`);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(String(id));
          return next;
        });
      } else if (confirm.mode === "bulk") {
        const { ids } = confirm.payload || {};
        const res = await bulkDelete(ids).unwrap();
        const deleted = res?.data?.deleted ?? res?.deleted ?? ids.length;
        toast.success(`Deleted ${deleted}`);
        setSelectedIds(new Set());
      }
    } catch (e) {
      const msg = e?.data?.msg || e?.error || "Operation failed";
      toast.error(msg);
    } finally {
      closeConfirm();
    }
  };

  const showSkeleton = isLoading && (!rows || rows.length === 0);

  return (
    <div className="bg-white shadow-md rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center px-4 py-3 bg-gradient-to-r from-[#8a79d7] to-[#6b59c6] text-white">
        <h2 className="text-lg font-semibold">Loose Stones</h2>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search (stone, shape, color, etc.)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 rounded-md text-sm text-gray-800 focus:outline-none"
          />

          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="px-3 py-1.5 rounded-md text-sm text-gray-800"
            title="Rows per page"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>

          <button
            onClick={openFilters}
            className="bg-white/20 border border-white/30 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-white/30 flex items-center gap-2"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>

          <Link
            to={"/stocks/loose-stones/create"}
            className="bg-white text-[#6b59c6] px-4 py-1.5 rounded-md text-sm font-medium hover:bg-violet-100"
          >
            + Add Entry
          </Link>
        </div>
      </div>

      {/* Thin progress bar */}
      {isFetching && (
        <div className="h-1 w-full bg-gradient-to-r from-[#8a79d7] via-[#6b59c6] to-[#8a79d7] animate-pulse" />
      )}

      {/* Active filter chips */}
      {chips.length > 0 && (
        <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-2 text-xs bg-white border px-2 py-1 rounded-full shadow-sm"
            >
              {c.label}
              <button
                className="text-slate-500 hover:text-red-500"
                onClick={() => clearChip(c.key)}
                title="Clear"
              >
                <FaTimesCircle />
              </button>
            </span>
          ))}
          <button
            className="ml-auto text-xs text-[#6b59c6] hover:underline"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="px-4 py-2 bg-amber-50 border-b flex items-center gap-3">
          <div className="text-sm">
            Selected: <b>{selectedIds.size}</b>
          </div>
          <button
            disabled={deletingMany}
            onClick={openConfirmBulk}
            className="ml-auto inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded"
          >
            {deletingMany ? "Deleting..." : "Delete Selected"}
          </button>
        </div>
      )}

      {/* Table */}
      <div
        className="relative overflow-x-auto max-h-[calc(100vh-260px)] overflow-y-auto min-h-[320px]"
        aria-busy={isFetching}
      >
        <table className="min-w-full text-sm text-left">
          <thead className="bg-[#ece8fb] text-[#4b3d91] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={allSelectedOnPage}
                  onChange={toggleSelectAllOnPage}
                  title="Select all on page"
                />
              </th>
              <th className={headerCell}>Stone {sortIcon("stoneName")}</th>
              <th className={headerCell}>Shape {sortIcon("shapeName")}</th>
              <th className={headerCell}>Color {sortIcon("colorName")}</th>
              <th className={headerCell}>Size {sortIcon("sizeName")}</th>
              <th className={headerCell}>Setting {sortIcon("settingTypeName")}</th>
              <th className={headerCell}>Unit {sortIcon("unit")}</th>
              <th className={headerCell}>Wt (g) {sortIcon("wt")}</th>
              <th className={headerCell}>Pcs {sortIcon("pcs")}</th>
              <th className={headerCell}>Vendor {sortIcon("vendorName")}</th>
              <th className={headerCell}>Branch {sortIcon("branchName")}</th>
              <th className={headerCell}>Created On {sortIcon("createdAt")}</th>
              <th className={headerCell}>Action</th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence initial={false}>
              {showSkeleton ? (
                <SkeletonRows cols={13} rows={8} />
              ) : rows?.length > 0 ? (
                rows.map((item, index) => {
                  const checked = selectedIds.has(String(item._id));
                  return (
                    <motion.tr
                      key={item._id || `${item.stoneName}-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className="border-t hover:bg-[#f6f3ff]"
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={checked}
                          onChange={() => toggleSelect(item._id)}
                        />
                      </td>
                      <td className={`${dataCell} font-medium`}>{item.stoneName || "-"}</td>
                      <td className={dataCell}>{item.shapeName || "-"}</td>
                      <td className={dataCell}>{item.colorName || "-"}</td>
                      <td className={dataCell}>{item.sizeName || "-"}</td>
                      <td className={dataCell}>{item.settingTypeName || "-"}</td>
                      <td className={dataCell}>{item.unit || "-"}</td>
                      <td className={dataCell}>{item.wt ?? "-"}</td>
                      <td className={dataCell}>{item.pcs ?? "-"}</td>
                      <td className={dataCell}>{item.vendorName || "-"}</td>
                      <td className={dataCell}>{item.branchName || "-"}</td>
                      <td className={dataCell}>
                        {item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                      </td>
                      <td className={`${dataCell} whitespace-nowrap`}>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="text-[#6b59c6] text-sm p-2 rounded-full hover:bg-[#6b59c6]/10"
                          title="Edit"
                          onClick={() => navigate(`/stocks/loose-stones/${item._id}/edit`)}
                        >
                          <FaPencilAlt />
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="text-[#6b59c6] text-sm p-2 rounded-full hover:bg-[#6b59c6]/10"
                          title="Delete"
                          onClick={() => openConfirmSingle(item)}
                          disabled={deletingOne}
                        >
                          <FaTrashAlt />
                        </motion.button>
                      </td>
                    </motion.tr>
                  );
                })
              ) : (
                !isLoading &&
                !isFetching && (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan={13} className="text-center text-gray-500 py-6">
                      No matching records found.
                    </td>
                  </motion.tr>
                )
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Footer / Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
        <div className="text-sm text-gray-600">
          Page <b>{page}</b> / {totalPages} • Total: <b>{totalDocs}</b>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50"
            disabled={!hasPrev || isFetching || isLoading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50"
            disabled={!hasNext || isFetching || isLoading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {isFilterOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <motion.div
            className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-3xl"
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button
                onClick={resetDraftFilters}
                className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border hover:bg-gray-50"
                title="Reset all"
              >
                <FiRotateCcw /> Reset
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Vendor</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={vendorOptions}
                  value={
                    vendorOptions.find((o) => String(o.value) === String(draftFilters.vendorId)) ||
                    null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, vendorId: opt?.value || "" }))
                  }
                  placeholder="Select vendor…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Branch</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={branchOptions}
                  value={
                    branchOptions.find((o) => String(o.value) === String(draftFilters.branchId)) ||
                    null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, branchId: opt?.value || "" }))
                  }
                  placeholder="Select branch…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Stone</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={stoneOptions}
                  value={
                    stoneOptions.find((o) => String(o.value) === String(draftFilters.stoneId)) ||
                    null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, stoneId: opt?.value || "" }))
                  }
                  placeholder="Select stone…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Shape</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={shapesOptions}
                  value={
                    shapesOptions.find((o) => String(o.value) === String(draftFilters.shape)) ||
                    null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, shape: opt?.value || "" }))
                  }
                  placeholder="Select shape…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Color</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={colorsOptions}
                  value={
                    colorsOptions.find((o) => String(o.value) === String(draftFilters.color)) ||
                    null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, color: opt?.value || "" }))
                  }
                  placeholder="Select color…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Size</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={sizesOptions}
                  value={
                    sizesOptions.find((o) => String(o.value) === String(draftFilters.size)) ||
                    null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, size: opt?.value || "" }))
                  }
                  placeholder="Select size…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">Setting Type</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={settingTypeOptions}
                  value={
                    settingTypeOptions.find(
                      (o) => String(o.value) === String(draftFilters.settingType)
                    ) || null
                  }
                  onChange={(opt) =>
                    setDraftFilters((s) => ({ ...s, settingType: opt?.value || "" }))
                  }
                  placeholder="Select setting…"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500">From</label>
                <input
                  type="date"
                  value={draftFilters.dateFrom}
                  onChange={(e) =>
                    setDraftFilters((s) => ({ ...s, dateFrom: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-md border"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">To</label>
                <input
                  type="date"
                  value={draftFilters.dateTo}
                  onChange={(e) =>
                    setDraftFilters((s) => ({ ...s, dateTo: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-md border"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setIsFilterOpen(false)}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 rounded-md bg-[#6b59c6] text-white"
              >
                Apply & Run
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirm.open}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
        title={
          confirm.mode === "bulk"
            ? `Delete ${confirm.payload?.count || 0} item(s)?`
            : `Delete ${confirm.payload?.label || "item"}?`
        }
        message={
          confirm.mode === "bulk"
            ? "Are you sure? Deleted records cannot be retrieved."
            : "Are you sure? Deleted record cannot be retrieved."
        }
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        loading={deletingOne || deletingMany}
      />
    </div>
  );
};

export default LooseStoneList;
